import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Loader2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { CourseContentBlockRenderer } from "@/modules/learningCenter/features/CourseDirectory/components/CourseContentBlockRenderer/CourseContentBlockRenderer";
import { CourseCompletionCelebration } from "@/modules/learningCenter/features/CourseDirectory/components/CourseCompletionCelebration/CourseCompletionCelebration";
import {
  isEnrollmentActive,
  useCourseViewer,
} from "@/modules/learningCenter/features/CourseDirectory/hooks/useCourseViewer/useCourseViewer";
import {
  buildModuleStatusMap,
  formatProgressPercent,
} from "@/modules/learningCenter/features/CourseDirectory/utils/progress";

/**
 * CoursePlayer — focused "learning mode".
 *
 * Renders OUTSIDE the standard app shell (see LearningCenterRoutes) as a
 * near-full-viewport learning view, mirroring the Udemy / Coursera / LinkedIn
 * Learning pattern: a slim branded top bar (so the header never just
 * "vanishes"), a left "Course content" outline, and a focused content column.
 */
export function CoursePlayer() {
  const { courseUuid = "", moduleUuid = "" } = useParams<{
    courseUuid: string;
    moduleUuid: string;
  }>();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const {
    viewer,
    isLoading,
    isMutating,
    justCompleted,
    acknowledgeCompletion,
    completeModule,
    resetModule,
    setLastAccessed,
  } = useCourseViewer(courseUuid);

  const modules = useMemo(() => viewer?.modules ?? [], [viewer?.modules]);
  const statusMap = useMemo(
    () => buildModuleStatusMap(viewer?.moduleProgress ?? []),
    [viewer?.moduleProgress],
  );

  const currentIndex = modules.findIndex((m) => m.uuid === moduleUuid);
  const currentModule = currentIndex >= 0 ? modules[currentIndex] : undefined;
  const active = isEnrollmentActive(viewer?.enrollment ?? null);

  const goToCourse = () =>
    navigate(`/hub/learning-center/courses/${courseUuid}`);

  const goToModule = (uuid: string) => {
    navigate(`/hub/learning-center/courses/${courseUuid}/modules/${uuid}`);
  };

  // Keep the resume pointer in sync as the learner moves between modules. Keyed
  // on the route's moduleUuid (a stable primitive) and whether the module is
  // present, so it fires exactly once per navigation. setLastAccessed is a
  // stable useCallback.
  const moduleExists = currentIndex >= 0;
  useEffect(() => {
    if (active && moduleExists && moduleUuid) {
      void setLastAccessed(moduleUuid);
    }
  }, [moduleUuid, moduleExists, active, setLastAccessed]);

  // Loading / not-found — full-screen so the focused layout is consistent
  // before data arrives.
  if (isLoading || !viewer) {
    return (
      <PlayerShell>
        <div className="flex flex-1 items-center justify-center text-gray-500 dark:text-gray-400">
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <span>Course not found.</span>
          )}
        </div>
      </PlayerShell>
    );
  }

  // Enroll gate: a learner with no active enrollment cannot use the player.
  if (!active) {
    return (
      <PlayerShell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-lg font-normal text-gray-900 dark:text-white">
            Enrollment required
          </h1>
          <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
            You need to enroll in this course before you can take it.
          </p>
          <Button onClick={goToCourse}>Go to course</Button>
        </div>
      </PlayerShell>
    );
  }

  if (!currentModule) {
    return (
      <PlayerShell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-lg font-normal text-gray-900 dark:text-white">
            Module not found
          </h1>
          <Button onClick={goToCourse}>Back to course</Button>
        </div>
      </PlayerShell>
    );
  }

  const isCurrentComplete = statusMap[currentModule.uuid] === "completed";
  const percent = viewer.enrollment
    ? formatProgressPercent(viewer.enrollment.progressPercentage)
    : 0;
  const prevModule = currentIndex > 0 ? modules[currentIndex - 1] : undefined;
  const nextModule =
    currentIndex < modules.length - 1 ? modules[currentIndex + 1] : undefined;
  const blocks = [...currentModule.blocks].sort(
    (a, b) => a.orderNumber - b.orderNumber,
  );

  const handleMarkComplete = () => {
    void completeModule(currentModule.uuid).then(() => {
      if (nextModule) {
        goToModule(nextModule.uuid);
      }
    });
  };

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 text-foreground">
      {/* Slim sticky branded top bar — replaces the "blank header" with an
          intentional learning-mode chrome. */}
      <header className="z-20 flex h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-slate-700 bg-slate-900 px-3 text-white sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <button
            aria-label={
              sidebarOpen ? "Hide course content" : "Show course content"
            }
            className="hidden flex-shrink-0 rounded-md p-1.5 text-gray-300 transition-colors hover:bg-slate-800 hover:text-white lg:inline-flex"
            onClick={() => setSidebarOpen((v) => !v)}
            type="button"
          >
            {sidebarOpen ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
          </button>
          <button
            className="flex flex-shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-gray-300 transition-colors hover:bg-slate-800 hover:text-white"
            onClick={goToCourse}
            type="button"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Exit to course</span>
          </button>
          <div
            aria-hidden="true"
            className="hidden h-5 w-px bg-slate-700 sm:block"
          />
          <h1 className="truncate text-sm font-normal text-white">
            {viewer.title}
          </h1>
        </div>

        <div className="flex flex-shrink-0 items-center gap-3">
          <span className="hidden text-xs text-gray-400 sm:inline">
            Module {currentIndex + 1} of {modules.length}
          </span>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-700">
              <div
                aria-label="Course progress"
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={percent}
                className="h-full rounded-full bg-primary transition-all duration-300"
                role="progressbar"
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-gray-300">
              {percent}%
            </span>
          </div>
        </div>
      </header>

      {/* Body: outline sidebar + content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Left outline — "Course content". */}
        {sidebarOpen && (
          <aside className="hidden w-72 flex-shrink-0 flex-col overflow-y-auto border-r border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 lg:flex">
            <div className="border-b border-gray-200 dark:border-slate-700 px-4 py-3">
              <h2 className="text-base font-normal text-gray-900 dark:text-white">
                Course content
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {modules.length} modules · {percent}% complete
              </p>
            </div>
            <nav aria-label="Course content" className="flex-1 p-2">
              {modules.map((module, index) => {
                const isComplete = statusMap[module.uuid] === "completed";
                const isCurrent = module.uuid === currentModule.uuid;
                return (
                  <button
                    aria-current={isCurrent ? "true" : undefined}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors",
                      isCurrent
                        ? "bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800/50",
                    )}
                    key={module.uuid}
                    onClick={() => goToModule(module.uuid)}
                    type="button"
                  >
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                      {isComplete ? (
                        <CheckCircleIcon
                          aria-label="Completed"
                          className="h-5 w-5 text-green-600 dark:text-green-400"
                        />
                      ) : (
                        <span
                          className={cn(
                            "text-xs",
                            isCurrent
                              ? "text-gray-700 dark:text-gray-200"
                              : "text-gray-400 dark:text-gray-500",
                          )}
                        >
                          {index + 1}
                        </span>
                      )}
                    </span>
                    <span className="flex-1">{module.title}</span>
                  </button>
                );
              })}
            </nav>
          </aside>
        )}

        {/* Main content column. */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 lg:hidden">
              Module {currentIndex + 1} of {modules.length}
            </p>
            <h2 className="mt-1 text-2xl font-normal text-gray-900 dark:text-white">
              {currentModule.title}
            </h2>
            {currentModule.description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {currentModule.description}
              </p>
            )}

            <div className="mt-6 space-y-2">
              {blocks.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This module has no content yet.
                </p>
              ) : (
                blocks.map((block) => (
                  <CourseContentBlockRenderer block={block} key={block.uuid} />
                ))
              )}
            </div>

            {/* Mark complete / reset — centered green CTA. */}
            <div className="mt-8 flex flex-col items-center gap-3 border-t border-gray-200 dark:border-slate-700 pt-6">
              {isCurrentComplete ? (
                <>
                  <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4" />
                    Completed
                  </span>
                  <Button
                    disabled={isMutating}
                    onClick={() => void resetModule(currentModule.uuid)}
                    variant="outline"
                  >
                    Reset
                  </Button>
                </>
              ) : (
                <Button
                  className="min-w-[180px] bg-green-600 text-white hover:bg-green-700"
                  disabled={isMutating}
                  onClick={handleMarkComplete}
                >
                  {isMutating ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-1.5 h-4 w-4" />
                  )}
                  {isMutating ? "Completing…" : "Mark Complete"}
                </Button>
              )}
            </div>

            {/* Prev / Next */}
            <div className="mt-6 flex items-center justify-between">
              <Button
                disabled={!prevModule}
                onClick={() => prevModule && goToModule(prevModule.uuid)}
                variant="outline"
              >
                <ArrowLeftIcon className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <Button
                disabled={!nextModule}
                onClick={() => nextModule && goToModule(nextModule.uuid)}
                variant="outline"
              >
                Next
                <ArrowRightIcon className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </main>
      </div>

      <CourseCompletionCelebration
        courseTitle={viewer.title}
        isOpen={justCompleted}
        onClose={acknowledgeCompletion}
        onViewTranscript={() => {
          acknowledgeCompletion();
          navigate("/hub/learning-center/my-courses");
        }}
      />
    </div>
  );
}

/**
 * Shared full-screen chrome for the player's loading / gate states so they
 * share the focused layout's framing (and a minimal top bar) before content is
 * available.
 */
function PlayerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 text-foreground">
      <header className="flex h-14 flex-shrink-0 items-center border-b border-slate-700 bg-slate-900 px-4 text-white">
        <span className="text-sm font-normal text-white">Learning Center</span>
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

export default CoursePlayer;
